import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { useLanguage } from "../../i18n/LanguageContext";
import { pathFor } from "../../i18n/seoMeta";

export const Header = () => {
  const { lang, t, otherLangPath } = useLanguage();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const homePath = pathFor("home", lang);
  const isHomePage = pathname === homePath;

  // All nav items now scroll to anchors on the homepage. From a subpage
  // (e.g. /sluzby/*), the <Link to="/#anchor"> first navigates home then
  // scrolls into view. Real anchor `<a>` hrefs keep them SEO-crawlable.
  const NAV_LINKS = [
    { key: "services", label: t.nav.services, type: "anchor", id: "services" },
    { key: "references", label: t.nav.references, type: "anchor", id: "references" },
    { key: "contact", label: t.nav.contact, type: "anchor", id: "contact" },
  ];

  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-spy only runs on the homepage where anchor sections actually exist.
  useEffect(() => {
    if (!isHomePage) {
      setActiveKey(null);
      return undefined;
    }
    const keys = ["services", "references", "contact"];
    const onScroll = () => {
      const y = window.scrollY + 140;
      for (let i = keys.length - 1; i >= 0; i--) {
        const el = document.getElementById(keys[i]);
        if (el && el.offsetTop <= y) {
          setActiveKey(keys[i]);
          return;
        }
      }
      setActiveKey(null);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHomePage]);

  const handleAnchorClick = (id) => {
    setOpen(false);
    if (isHomePage) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // navigate to homepage with hash, then scroll after route mount
      navigate(`${homePath}#${id}`);
    }
  };

  // After cross-page navigation with #hash, scroll into view on mount.
  useEffect(() => {
    if (isHomePage && window.location.hash) {
      const id = window.location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 80);
    }
  }, [isHomePage]);

  const LangSwitch = ({ size = "md" }) => {
    const base =
      size === "md" ? "px-3 py-1 text-xs md:text-sm" : "px-3 py-1.5 text-sm";
    const baseClass =
      "font-heading font-bold tracking-[0.15em] uppercase transition-colors";
    const activeClass = "bg-[#FFE21A] text-[#181B24]";
    const inactiveClass = "text-white/80 hover:text-white";
    const csPath = otherLangPath && lang === "en" ? otherLangPath : pathname;
    const enPath = otherLangPath && lang === "cs" ? otherLangPath : pathname;
    return (
      <div
        data-testid="lang-switch"
        className="inline-flex items-center rounded-full bg-white/10 border border-white/15 overflow-hidden"
      >
        <Link
          to={csPath}
          data-testid="lang-switch-cs"
          aria-current={lang === "cs"}
          className={`${base} ${baseClass} ${lang === "cs" ? activeClass : inactiveClass}`}
        >
          CZ
        </Link>
        <Link
          to={enPath}
          data-testid="lang-switch-en"
          aria-current={lang === "en"}
          className={`${base} ${baseClass} ${lang === "en" ? activeClass : inactiveClass}`}
        >
          EN
        </Link>
      </div>
    );
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#202535]/95 backdrop-blur-md border-b border-white/5"
          : "bg-[#202535]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-3 md:py-4 flex items-center justify-between gap-4">
        <Link to={homePath} aria-label="IT Impact home">
          <Logo />
        </Link>

        <nav
          data-testid="desktop-nav"
          className="hidden lg:flex items-center gap-6 xl:gap-10"
        >
          {NAV_LINKS.map((l) =>
            l.type === "route" ? (
              <Link
                key={l.key}
                to={l.to}
                data-testid={`nav-link-${l.key}`}
                className={`font-heading text-sm xl:text-base tracking-[0.18em] font-medium transition-colors duration-200 ${
                  pathname === l.to || pathname.startsWith(l.to)
                    ? "text-[#FFE21A]"
                    : "text-white hover:text-[#FFE21A]"
                }`}
              >
                {l.label}
              </Link>
            ) : (
              <Link
                key={l.key}
                to={`${homePath}#${l.id}`}
                onClick={(e) => {
                  if (isHomePage) {
                    e.preventDefault();
                    handleAnchorClick(l.id);
                  }
                }}
                data-testid={`nav-link-${l.key}`}
                className={`font-heading text-sm xl:text-base tracking-[0.18em] font-medium transition-colors duration-200 ${
                  activeKey === l.key
                    ? "text-[#FFE21A]"
                    : "text-white hover:text-[#FFE21A]"
                }`}
              >
                {l.label}
              </Link>
            ),
          )}
          <LangSwitch />
        </nav>

        <div className="lg:hidden flex items-center gap-3">
          <LangSwitch size="sm" />
          <button
            data-testid="mobile-menu-toggle"
            className="text-white p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-nav"
          className="lg:hidden bg-[#202535] border-t border-white/5"
        >
          <div className="flex flex-col px-6 py-4 gap-1">
            {NAV_LINKS.map((l) =>
              l.type === "route" ? (
                <Link
                  key={l.key}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.key}`}
                  className="text-left py-3 px-2 font-heading tracking-[0.18em] text-base border-b border-white/5 text-white"
                >
                  {l.label}
                </Link>
              ) : (
                <Link
                  key={l.key}
                  to={`${homePath}#${l.id}`}
                  onClick={(e) => {
                    if (isHomePage) {
                      e.preventDefault();
                      handleAnchorClick(l.id);
                    } else {
                      setOpen(false);
                    }
                  }}
                  data-testid={`mobile-nav-link-${l.key}`}
                  className={`text-left py-3 px-2 font-heading tracking-[0.18em] text-base border-b border-white/5 ${
                    activeKey === l.key ? "text-[#FFE21A]" : "text-white"
                  }`}
                >
                  {l.label}
                </Link>
              ),
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
